import { z } from 'zod';

import type { CycleCheckContext } from './conditionalLogicGraph';
import { findCycleVertices } from './conditionalLogicGraph';

const conditionalLogicRulesSchema = z
  .array(
    z.object({
      ifField: z.string().min(1, 'Field is required'),
      values: z.array(z.string()).min(1, 'At least one value is required'),
    })
  )
  .optional()
  .default([]);

const optionSchema = z.object({
  id: z.string(),
  label: z.string().min(1, 'Option label is required'),
});

export const OPTION_FIELD_TYPES = ['select', 'multiselect'] as const;

export const isOptionFieldType = (type: string): boolean =>
  (OPTION_FIELD_TYPES as readonly string[]).includes(type);

export const DEFAULT_VALUE_FIELD_TYPES = ['text', 'textarea', 'link'] as const;

export const supportsDefaultValue = (type: string): boolean =>
  (DEFAULT_VALUE_FIELD_TYPES as readonly string[]).includes(type);

export const sectionEditorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  conditionalLogicEnabled: z.boolean().default(false),
  conditionalLogicRules: conditionalLogicRulesSchema,
  guidanceEnabled: z.boolean().default(false),
  guidance: z.string().optional(),
});

export const fieldEditorSchema = z
  .object({
    fieldType: z.string().min(1, 'Field type is required'),
    fieldName: z.string().min(1, 'Field name is required'),
    required: z.boolean().optional().default(false),
    readOnly: z.boolean().optional().default(false),
    hidden: z.boolean().optional().default(false),
    defaultValueEnabled: z.boolean().optional().default(false),
    defaultValue: z.string().optional().default(''),
    options: z.array(optionSchema).optional().default([]),
    conditionalLogicEnabled: z.boolean().default(false),
    conditionalLogicRules: conditionalLogicRulesSchema,
    guidanceEnabled: z.boolean().default(false),
    guidance: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (isOptionFieldType(data.fieldType) && data.options.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least 2 options are required',
        path: ['options'],
      });
    }

    if (isOptionFieldType(data.fieldType)) {
      const labels = data.options.map((o) => o.label.trim().toLowerCase());
      const seen = new Set<string>();
      labels.forEach((label, index) => {
        if (seen.has(label)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Option labels must be unique',
            path: ['options', index, 'label'],
          });
        }
        seen.add(label);
      });
    }

    if (
      data.defaultValueEnabled &&
      (!data.defaultValue || data.defaultValue.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Default value is required',
        path: ['defaultValue'],
      });
    }

    if (
      data.fieldType === 'url' &&
      data.defaultValue &&
      data.defaultValue.length > 0
    ) {
      try {
        const url = new URL(data.defaultValue);
        Boolean(url);
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid URL',
          path: ['defaultValue'],
        });
      }
    }
  });

export type SectionEditorValues = z.infer<typeof sectionEditorSchema>;
export type FieldEditorValues = z.infer<typeof fieldEditorSchema>;

const CYCLE_ERROR_MESSAGE =
  'One or more rules create a circular dependency. Remove or change the rule(s) that cause this.';

const SELF_REFERENCE_MESSAGE = 'A field cannot depend on itself';

const SECTION_OWN_FIELD_MESSAGE =
  'A section cannot depend on a field inside the same section';

const formatCyclePathMessage = (
  cycleVertices: string[],
  getEntityLabel?: (vertexId: string) => string
): string => {
  const labels = cycleVertices.map(
    (v) => getEntityLabel?.(v) ?? v.replace(/^(field|section):/, '')
  );

  return `${CYCLE_ERROR_MESSAGE} Cycle: ${labels.join(' → ')}.`;
};

const cycleCheckRefine =
  (entityType: 'field' | 'section', ctx: CycleCheckContext | undefined) =>
  (
    data: {
      conditionalLogicEnabled: boolean;
      conditionalLogicRules?: { ifField: string }[];
    },
    zctx: z.RefinementCtx
  ) => {
    // Run the check whenever rules exist; a disabled toggle still produces a
    // live cycle the moment it's re-enabled.
    if (!ctx || !data.conditionalLogicRules?.length) {
      return;
    }

    if (entityType === 'field') {
      data.conditionalLogicRules.forEach((rule, idx) => {
        if (rule.ifField && rule.ifField === ctx.entityId) {
          zctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SELF_REFERENCE_MESSAGE,
            path: ['conditionalLogicRules', idx, 'ifField'],
          });
        }
      });
    }

    if (entityType === 'section') {
      const fieldsInSection = new Set(ctx.containers[ctx.entityId] ?? []);
      data.conditionalLogicRules.forEach((rule, idx) => {
        if (rule.ifField && fieldsInSection.has(rule.ifField)) {
          zctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SECTION_OWN_FIELD_MESSAGE,
            path: ['conditionalLogicRules', idx, 'ifField'],
          });
        }
      });
    }

    const cycleVertices = findCycleVertices({
      ...ctx,
      entityType,
      proposedRules: data.conditionalLogicRules,
    });
    if (cycleVertices) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatCyclePathMessage(cycleVertices, ctx.getEntityLabel),
        path: ['conditionalLogicRules'],
      });
    }
  };

export const createSectionEditorSchema = (ctx?: CycleCheckContext) =>
  sectionEditorSchema.superRefine(cycleCheckRefine('section', ctx));

export const createFieldEditorSchema = (ctx?: CycleCheckContext) =>
  fieldEditorSchema.superRefine(cycleCheckRefine('field', ctx));
